import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { toLocale } from "~/libs/locale";

const second = 1000;
const minute = second * 60;
const hour = minute * 60;
const day = hour * 24;
const month = day * 30;
const year = day * 365;

const defaultOptions: Intl.RelativeTimeFormatOptions = {
  style: "short",
  numeric: "auto",
};

// Refresh often enough for the displayed unit to stay accurate
const getRefreshDelay = (elapsed: number) => {
  if (elapsed < minute) return second;
  if (elapsed < hour) return 15 * second;
  if (elapsed < day) return minute;
  return hour;
};

const useRelativeTime = (
  time: number,
  locale: string,
  dateNow = Date.now,
  options: Intl.RelativeTimeFormatOptions = defaultOptions,
) => {
  const [now, setNow] = useState(dateNow);
  const [prevTime, setPrevTime] = useState(time);
  const { t } = useTranslation();

  // Resync `now` when the date changes, otherwise it is compared to an old `now`
  if (time !== prevTime) {
    setPrevTime(time);
    setNow(dateNow());
  }

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const schedule = (current: number) => {
      timeout = setTimeout(
        () => {
          const next = dateNow();
          setNow(next);
          schedule(next);
        },
        getRefreshDelay(Math.abs(current - time)),
      );
    };
    schedule(dateNow());
    return () => clearTimeout(timeout);
  }, [dateNow, time]);

  const formatter = useMemo(() => {
    return new Intl.RelativeTimeFormat(locale, options);
  }, [locale, options]);

  return useMemo(() => {
    const relativeTime = now - time;
    const elapsed = Math.abs(relativeTime);

    const sign = Math.sign(-relativeTime);
    if (elapsed < minute) {
      return t("date.now");
    } else if (elapsed < hour) {
      return formatter.format(sign * Math.round(elapsed / minute), "minute");
    } else if (elapsed < day) {
      return formatter.format(sign * Math.round(elapsed / hour), "hour");
    } else if (elapsed < month) {
      return formatter.format(sign * Math.round(elapsed / day), "day");
    } else if (elapsed < year) {
      return formatter.format(sign * Math.round(elapsed / month), "month");
    } else {
      return formatter.format(sign * Math.round(elapsed / year), "year");
    }
  }, [now, time, formatter, t]);
};

export function RelativeTime({
  dateTime,
  options,
}: {
  dateTime: Date;
  options?: Intl.RelativeTimeFormatOptions;
}) {
  const { i18n } = useTranslation();

  const relativeTime = useRelativeTime(
    dateTime.getTime(),
    toLocale(i18n.language),
    Date.now,
    options,
  );

  return <>{relativeTime}</>;
}
